/**
 * NavbarDirectiveController is a controller class for managing the navbar directive in the application.
 * It handles various functionalities such as calculator management, task information, and user interactions.
 */

import moment from 'moment';
export class NavbarDirectiveController {
    /**
     * Constructs the NavbarDirectiveController.
     * @param {Object} $scope - The AngularJS scope.
     * @param {Object} $rootScope - The root scope of the application.
     * @param {Object} $bridges - Service for bridge functionalities.
     * @param {Object} $location - Service for changing the URL in the browser.
     * @param {Object} $signals - Service for emitting signals.
     * @param {Object} $uibModal - Service for opening modal dialogs.
     * @param {Object} $compile - Service for compiling HTML strings.
     * @param {Object} $timeout - Service for executing functions after a timeout.
     * @param {Object} $http - Service for making HTTP requests.
     * @param {Object} $options - Options for the controller.
     * @param {Object} $route - Service for routing.
     * @param {Object} $translate - Service for translation.
     * @param {Object} UserInfoService - Service for user information.
     * @param {Object} TaskSelectionService - Service for task selection.
     * @param {Object} TaskCountdownService - Service for task countdown.
     * @param {Object} TaskInfoService - Service for task information.
     * @param {Object} TaskDataService - Service for task data.
     * @param {Object} IAAPreferencesService - Service for IAA preferences.
     * @param {Object} $routeParams - Parameters from the current route.
     * @param {Object} $ - jQuery library.
     */
    constructor($scope, $rootScope, $bridges, $location, $signals, $uibModal, $compile, $timeout, $http, $options, $route, $translate, UserInfoService, TaskSelectionService, TaskCountdownService, TaskInfoService, TaskDataService, IAAPreferencesService, $routeParams, $) {
        // Controller meta setup
        this.$scope = $scope;
        this.$rootScope = $rootScope;
        this.$bridges = $bridges;
        this.$location = $location;
        this.$signals = $signals;
        this.$uibModal = $uibModal;
        this.$compile = $compile;
        this.$timeout = $timeout;
        this.$http = $http;
        this.$options = $options;
        this.$route = $route;
        this.$translate = $translate;
        this.UserInfoService = UserInfoService;
        this.TaskSelectionService = TaskSelectionService;
        this.TaskCountdownService = TaskCountdownService;
        this.TaskInfoService = TaskInfoService;
        this.TaskDataService = TaskDataService;
        this.IAAPreferencesService = IAAPreferencesService;
        this.$routeParams = $routeParams;
        this.$ = $;

        this.$logger = $bridges.logger();
        this.$eventLog = $bridges.eventLog();

        this.$scope._logIdentifier = 'NavbarDirectiveController';
        this.$logger.logExit(this.$scope, 'initialisation');

        this.$scope.options = $options;

        // Scope attributes
        this.$scope.currentDateTime = new Date();
        this.$scope.dateFormatted = moment().format("MMMM Do, YYYY");

        // Calculator Setup
        this.$rootScope.showCalc = false;
        if (typeof (this.$rootScope.calculatorEverOpened) === 'undefined') {
            this.$rootScope.calculatorEverOpened = false;
        }

        // Move Calc to top left when the navbar is rendered
        this.repositionCalculator();

        // Hide Calc and disable Highlight mode on route change
        this.$scope.$on('$routeChangeStart', () => {
            this.$rootScope.showCalc = false;
            this.$rootScope.isHighlightMode = false;
            this.repositionCalculator();
        });

        // Private variables
        this.countDown = TaskCountdownService.getInstance();
        this.userInfo = UserInfoService.getUserInfo();
        this.taskInfo = TaskInfoService.getTaskInfo();
        this.taskUUID = TaskSelectionService.getTaskUUID();
        this.taskContext = TaskDataService.fetchContext();
        this.task = this.taskContext.read(this.taskUUID);
        this.candidateTask = this.taskInfo.getTaskDataByUUID(this.taskUUID);

        this.$scope.writingDirection = this.$rootScope.isJournal ? "ltr" : this.taskInfo.getInputLanguage().writingDirection;

        this.$scope.sheets = !angular.isFunction(this.task.isSheet) || !this.task.isSheet() ? this.task.getSheets() : null;

        this.$scope.taskInfo = this.taskInfo;
        if (this.userInfo.getType() == "candidate") {
            this.$scope.taskTimeFormatted = this.userInfo.getTaskTimeFormatted();
        }
        this.$scope.candidateCanPause = IAAPreferencesService.getPreference('candidateCanPause') && !this.countDown.isPaused();
        this.$scope.isMediaAssessment = angular.isFunction(this.taskInfo.isMediaAssessment) ? this.taskInfo.isMediaAssessment() : false;
        this.$scope.showFinishEarlyBtn = this.taskInfo.getPackageType() !== 'examiner';
        this.$scope.isFinishEarlyDisabled = () => {
            return this.$scope.isReadingtime()
                || this.countDown.getMillis('elapsed') < window.IB.timers.finishEarlyTime
                || this.countDown.getRemainingMillis() < window.IB.timers.finishEarlyTimeMin;
        };

        // Cache the candidate display name on the scope so template bindings
        // don't need to re-invoke getCompleteName() (with its string joins
        // and HTML encoding) on every digest. Watch the name parts so the
        // cache updates when session resume merges the stored candidate
        // identity back in.
        const updateCandidateDisplayName = () => {
            const c = this.$scope.candidate;
            this.$scope.candidateDisplayName =
                (c && typeof c.getCompleteName === 'function') ? c.getCompleteName() : '';
        };
        updateCandidateDisplayName();
        this.$scope.$watchGroup([
            () => {
                const c = this.$scope.candidate;
                return c && c.getFirstName ? c.getFirstName() : null;
            },
            () => {
                const c = this.$scope.candidate;
                return c && c.getLastName ? c.getLastName() : null;
            },
        ], updateCandidateDisplayName);

        if (angular.isDefined(this.$scope.$root.isCMS)) {
            this.$scope.isCMS = isCMS;
        }

        this.$scope.iframeTools = this.task.getIframeTools();
        this.$scope.iframeDefaultIcon = '../images/highlighttool_off.png';
        this.$scope.taskUUID = this.taskUUID;

        // Journal
        if (this.countDown.getMode() === 'journal') {
            this.$scope.mode = this.countDown.getMode();
        }

        this.$scope.isReadingtime = () => {
            return this.countDown.getMode() === 'readingTime';
        };

        this.$scope.isPaused = () => {
            return this.countDown.isPaused();
        };

        this.$scope.isInAdminMenu = () => this.isInAdminMenu();

        this.$scope.isCalculatorAvailable = () => {
            return !this.isInAdminMenu() && !this.$scope.isReadingtime() && !this.$scope.isPaused() && !this.$scope.isMediaAssessment;
        };



        this.unreg = this.$rootScope.$on("toggleCalculator", () => this.toggleCalculator());

        this.$scope.$on("$destroy", () => {
            if (this._isPauseCheckTimeout) {
                this.$timeout.cancel(this._isPauseCheckTimeout);
            }
            this.unreg();
        });

        this.$logger.logExit(this.$scope, 'initialisation');

        // Bind functions to scope so they can be called from the template
        this.$scope.onOverviewClick = () => this.onOverviewClick();
        this.$scope.toggleCalculator = () => this.toggleCalculator();
        this.$scope.toggleHightlightMode = () => this.toggleHightlightMode();
        this.$scope.toggleProtractorTool = () => this.toggleProtractorTool();
        this.$scope.onBookmarkPopupItemClick = (markedQuestion) => this.onBookmarkPopupItemClick(markedQuestion);
        this.$scope.onInvigilatorAccessClick = () => this.onInvigilatorAccessClick();
        this.$scope.getTaskTitle = () => this.getTaskTitle();
        this.$scope.getTaskUUID = () => this.getTaskUUID();
        this.$scope.onDropDownInstructionsClick = () => this.onDropDownInstructionsClick();
        this.$scope.onDropDownQuitClick = () => this.onDropDownQuitClick();
        this.$scope.onFinishEarlyClick = () => this.onFinishEarlyClick();
        this.$scope.onPauseClick = () => this.onPauseClick();

        this.$scope.bookmarkPopupItemDescription = (markedQuestion, rtl) => {
            if (!markedQuestion) {
                console.warn('markedQuestion is undefined in bookmarkPopupItemDescription');
                return '';
            }

            const sheet = this.task.findSheetWithQuestionPart(markedQuestion);
            if (!sheet) {
                console.warn('sheet not found for markedQuestion in bookmarkPopupItemDescription');
                return '';
            }

            const shortTitle = markedQuestion.getShortTitle() === '(Default Question)' ? '' : markedQuestion.getShortTitle();

            if (!rtl) {
                return markedQuestion.getTitle() ? markedQuestion.getTitle() : sheet.getShortTitle() + shortTitle;
            } else {
                return markedQuestion.getTitle() ? markedQuestion.getTitle() : shortTitle + sheet.getShortTitle();
            }
        };
    }

    /**
     * Repositions the calculator to the top left of the screen.
     */
    repositionCalculator() {
        const calcContainer = this.$('#calculatorContainer');
        if (calcContainer.length) {
            calcContainer.css({
                'position': 'fixed',
                'left': '60px',  // Adjusted position
                'top': '60px',   // Adjusted position
                'z-index': '9999'
            });
        }
    }

    /**
     * Animates the scroll to a target UUID.
     * @param {string} targetUUID - The UUID of the target to scroll to.
     */
    scrollAnimation(targetUUID) {
        var targetSelector = 'question-headline[question="' + targetUUID + '"]',
            halfViewport = $(window).height() * 0.01, // kept just in case we go back to this. Default factor: * 0.5
            scrollTopTo = $(document).scrollTop(),
            targetY = $(document).scrollTop();
        if ($(targetSelector).length > 0) {
            targetY = $(targetSelector).position().top;

            if (targetY > halfViewport) {
                targetY = targetY - halfViewport;
            }
        }
        scrollTopTo = targetY;

        $('html, body').stop().animate({
            'scrollTop': scrollTopTo
        }, 900, 'swing');
    }

    /**
     * Handles the click event on a bookmark popup item.
     * @param {Object} markedQuestion - The marked question object.
     */
    onBookmarkPopupItemClick(markedQuestion) {
        this.$logger.logEnter(this.$scope, 'onBookmarkPopupItemClick');

        var sheet = this.$scope.task.findSheetWithQuestionPart(markedQuestion),
            newSheetUrl = '/sheet/' + sheet.getUUID() + '/' + markedQuestion.getUUID();

        if (this.$routeParams.sheetUUID !== sheet.getUUID()) {
            this.$location.path(newSheetUrl);
        } else {
            this.scrollAnimation(markedQuestion.getUUID());
        }
    }

    /**
     * Checks if the countdown is paused and handles the pause logic.
     * SMELL: This method is recursive and uses a timeout, which can lead to performance issues if not managed properly.
     */
    _isPauseCheck() {
        if (this.countDown.isPaused()) {
            if (this._isPauseCheckTimeout) {
                this.$timeout.cancel(this._isPauseCheckTimeout);
            }
            this._isPauseCheckTimeout = this.$timeout(() => {
                if (!this.$route.current.regexp.test("/pause/:taskUUID") &&
                    !this.$route.current.regexp.test('/invigilatorMenu/:taskUUID') &&
                    !this.$route.current.regexp.test('/invigilatorMenu/:taskUUID/:returnTo')) {
                    if (this.$routeParams.sheetUUID) {
                        this.$location.path('/pause/' + this.$routeParams.sheetUUID);
                    } else {
                        this.$location.path('/pause/' + this.taskUUID);
                    }
                }
                this._isPauseCheck();
            }, 1000, false);
        }
    }

    /**
     * Handles the pause click event.
     */
    onPauseClick() {
        if (this.$scope.candidateCanPause) {
            this.$eventLog.logEvent(this.$translate.instant("MODULES.ADMIN.EVENTLOG_PAUSE_BY_CANDIDATE"), "pauseByCandidate", "", "", {});
            this.countDown.togglePause();
            this._isPauseCheck();
            if (this.$routeParams.sheetUUID) {
                this.$location.path('/pause/' + this.$routeParams.sheetUUID);
            } else {
                this.$location.path('/pause/' + this.taskUUID);
            }
            return;
        }
    }

    /**
     * Handles the overview click event.
     */
    onOverviewClick() {
        this.$logger.logEnter(this.$scope, 'onOverviewClick');
        this.$location.path('/overview/' + this.task.getUUID());
    }

    /**
     * Handles the dropdown reset time click event.
     */
    onDropDownResetTimeClick() {
        this.$logger.logEnter(this.$scope, 'onDropDownResetTimeClick');
        this.$logger.info("Resetting user's available time");
        this.countDown.resetTime(this.userInfo.getTaskTime());
        this.countDown.startMeasure();
    }

    /**
     * Handles the dropdown expire time click event.
     */
    onDropDownExpireTimeClick() {
        this.$logger.logEnter(this.$scope, 'onDropDownExpireTimeClick');
        this.countDown.resetTime(2000);
        this.countDown.startMeasure();
    }

    /**
     * Handles the finish early click event.
     */
    onFinishEarlyClick() {
        if (!this.$scope.showFinishEarlyBtn || this.$scope.isMediaAssessment || this.$scope.isFinishEarlyDisabled()) return false;
        var modalInstance = this.$uibModal.open({
            templateUrl: "/views/ib/modules/navbar/modal/finishEarlyModal.html",
            windowClass: "finish-early-modal",
            transclude: false,
            backdrop: 'static',
            keyboard: false,
            controller: 'FinishEarlyModalController',
            resolve: {
                task: () => this.task,
                countDown: () => this.countDown
            }
        });
    }

    /**
     * Handles the dropdown quit click event.
     */
    onDropDownQuitClick() {
        this.$signals.emit(IB.Signals.SHOW_MODAL, {
            headline: this.$translate.instant('MODULES.NAVBAR.DIRECTIVEQUIT.HEADLINE'),
            text: this.$translate.instant('MODULES.NAVBAR.DIRECTIVEQUIT.CONTENT'),
            cancelText: this.$translate.instant('BTN.CANCEL'),
            buttons: {
                'BTN.QUIT': () => {
                    this.$logger.info("Quit confirmed in modal dialog");
                    this.$bridges.runtime().quit(false);
                    if (typeof this.$uibModalInstance !== 'undefined') {
                        this.$uibModalInstance.close();
                    }
                },
            },
            onCloseCallback: () => {
                this.$logger.info("Cancelling modal");
                if (typeof this.$uibModalInstance !== 'undefined') {
                    this.$uibModalInstance.dismiss('cancel');
                }
            }
        });
    }

    /**
     * Handles the dropdown instructions click event.
     */
    onDropDownInstructionsClick() {
        var newText = '';

        var file = this.$scope.isMediaAssessment ? 'NavbarDirectiveInstructionsMedia.html' : 'NavbarDirectiveInstructions.html';

        this.$http({ method: 'GET', url: '/views/ib/modules/navbar/' + file })
            .success((data, status, headers, config) => {
                newText = this.$compile(data)(this.$scope);
            })
            .error((data, status, headers, config) => {
                // called asynchronously if an error occurs
                // or server returns response with an error status.
            })
            .then(() => {
                this.$timeout(() => {
                    this.$signals.emit(IB.Signals.SHOW_MODAL, {
                        headline: this.$translate.instant('BTN.INSTRUCTIONS'),
                        text: newText.html(),
                        cancelText: this.$translate.instant('BTN.CLOSE'),
                        onCloseCallback: () => {
                        }
                    });
                }, 1);
            });
    }

    /**
     * Checks if the current route is in the admin menu.
     * @returns {boolean} True if in admin menu, false otherwise.
     */
    isInAdminMenu() {
        return this.$route.current.params.returnTo ? true : false;
    }

    /**
     * Handles the invigilator access click event.
     */
    onInvigilatorAccessClick() {
        var path = btoa(this.$location.path());
        this.$location.path('/invigilatorMenu/' + this.taskUUID + '/' + path);
    }

    /**
     * Gets the task title.
     * @returns {string} The task title.
     */
    getTaskTitle() {
        return this.taskInfo.getSubjectOrTitle();
    }

    /**
     * Toggles the calculator visibility.
     */
    toggleCalculator() {
        // Ensure calculator is properly initialized
        if (typeof this.$rootScope.showCalc === 'undefined') {
            this.$rootScope.showCalc = false;
        }

        // Toggle calculator visibility
        this.$rootScope.showCalc = !this.$rootScope.showCalc;

        if (this.$rootScope.showCalc) {
            this.$rootScope.calculatorEverOpened = true;

            // Position calculator and set focus
            this.$timeout(() => {
                this.repositionCalculator();
                this.focusCalculator();
            }, 100);
        }
    }

    /**
     * Toggles the highlight mode.
     */
    toggleHightlightMode() {
        this.$rootScope.isHighlightMode = !this.$rootScope.isHighlightMode;
    }

    /**
     * Focuses the calculator input.
     */
    focusCalculator() {
        const calcInput = this.$('#calculatorDiv input');
        if (calcInput.length) {
            calcInput.focus();
        }
    }

    /**
     * Toggles the protractor tool.
     */
    toggleProtractorTool() {
        this.$scope.protractorToolActive = !this.$scope.protractorToolActive;
        this.$scope.$broadcast("toggleProtractorTool", this.$scope.protractorToolActive);
    }

    /**
     * Gets the task UUID.
     * @returns {string} The UUID of the task.
     */
    getTaskUUID() {
        return this.taskUUID;
    }
}

/**
 * Registers a navbar directive with the provided module.
 * 
 * @param {object} module - The AngularJS module to which the directive will be registered.
 */
export default function registerNavbarDirective(module) {
    module.directive('navbar', function ($compile) {
        return {
            restrict: 'E', // Restricts the directive to element usage only.
            replace: true, // Replaces the HTML element with the template.
            templateUrl: '/views/ib/modules/navbar/NavbarDirective.html', // Path to the template file.
            controller: NavbarDirectiveController // Controller associated with the directive.
        };
    });
}
